/**
 * Load the data needed to render the Job Launch drawer contents.
 */

import {Alert} from '@wandb/weave/components/Alert';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {useEffect} from 'react';

import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {
  useAvailableJobOptions,
  useProjectView,
  useViewerQueueOptions,
} from '../hooks';
import {useJobDetails, useJobDetailsNoPoll} from '../JobDetail/hooks';
import {JobDetails} from '../JobDetail/utilTypes';
import {JobOption, SelectJob} from '../SelectJob';
import {SelectJobVersion} from '../SelectJobVersion';
import {TruncatedMarkdown} from '../TruncatedMarkdown';
import {LaunchSpec} from '../utilTypes';
import * as S from './JobDrawerContent.styles';
import {
  getVersionDescription,
  JobDrawerContentInner,
} from './JobDrawerContentInner';

// Jobs with this access type are considered private
const PRIVATE_ACCESS_TERMS = ['ENTITY_READ', 'PRIVATE'];

export type JobDrawerContentProps = React.PropsWithChildren<{
  // For passing, e.g., scheduler selection when opened from sweeps-on-launch surface.
  // These should be rendered at the bottom under an "Additional Settings" section
  // Parent fully owns these components and their state.
  // children: React.ReactNode;
  //

  closeDrawer: () => void;

  entityName: string;
  projectName: string;

  open: boolean;

  // Allow preselection of job / version / overrides, e.g., when we are
  // embedded somewhere that already fetched this info.
  defaultJobId?: string;
  defaultJobVersion?: string;
  defaultRunToClone?: string;

  // optional hint text underneath override bin
  overrideHintText?: string;

  finalizeCTA?: string; // Bottom right button text
  onFinalize?: (spec: LaunchSpec) => void; // Default: throws
}>;

export const JobDrawerContent = ({
  entityName,
  projectName,
  defaultJobId,
  defaultJobVersion,
  defaultRunToClone,
  overrideHintText,
  finalizeCTA,
  closeDrawer,
  onFinalize,
  children,
  open,
}: JobDrawerContentProps) => {
  const {jobOptions: options} = useAvailableJobOptions(entityName, projectName);
  const [jobId, setJobId] = React.useState<JobOption | undefined>();
  const userSelectedJobId = React.useRef(false);
  useEffect(() => {
    // HACK: reset userSelectedJobId when the drawer is closed
    // so that the defaultJobId is used when the drawer is reopened
    if (!open) {
      userSelectedJobId.current = false;
    }
  }, [open]);

  // useEffect to capture setState change after options are loaded
  // since options are loaded incrementally, this can run several times
  // and selects the default option as soon as it is available. However,
  // it's possible the user selected a different option before the default
  // option was loaded, so we only set the default if the user hasn't
  // already selected a job.
  useEffect(() => {
    if (!userSelectedJobId.current && defaultJobId != null) {
      const optionToSelect = options.find(o => o.value === defaultJobId);
      if (optionToSelect != null) {
        setJobId(optionToSelect);
      }
    }
  }, [defaultJobId, options]);

  const [jobVersion, setJobVersion] = React.useState<string>(
    defaultJobVersion ?? ''
  );
  // This is required to make the correct default job version appear
  // on first load when cloning a run.
  useEffect(() => {
    setJobVersion(defaultJobVersion ?? '');
  }, [defaultJobVersion]);

  return (
    <S.JobDrawer>
      <S.CenteredFlex>
        {jobId ? (
          <S.Header>Launch "{jobId?.label}"</S.Header>
        ) : (
          <S.Header>Launch</S.Header>
        )}
        <S.CloseButton>
          <Button
            data-test="launch-drawer-close"
            onClick={() => {
              closeDrawer();
            }}
            icon="close"
            size="large"
            variant="ghost"
          />
        </S.CloseButton>
      </S.CenteredFlex>
      <>
        <S.LabelRow>
          <S.Label>Job</S.Label>
        </S.LabelRow>
        <SelectJob
          entityName={entityName}
          projectName={projectName}
          selectedJobId={jobId ?? null}
          onSelectJob={alias => {
            userSelectedJobId.current = true;
            setJobId(alias);
            setJobVersion(defaultJobVersion ?? '');
          }}
        />
      </>
      {jobId != null && (
        <>
          <JobDrawerVersionContent
            jobId={jobId.value}
            jobVersion={jobVersion}
            defaultRunToClone={defaultRunToClone}
            // Job options loaded from the project won't have an entity set,
            // so use the entity of the JobDrawerContent
            entityName={jobId.entity ?? entityName}
            projectName={jobId.project ?? projectName}
            overrideHintText={overrideHintText}
            finalizeCTA={finalizeCTA}
            setJobVersion={setJobVersion}
            closeDrawer={() => {
              closeDrawer();
            }}
            onFinalize={onFinalize}>
            {children}
          </JobDrawerVersionContent>
        </>
      )}
    </S.JobDrawer>
  );
};

type JobDrawerVersionContentProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  jobId: string;
  jobVersion: string;
  defaultRunToClone?: string;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  setJobVersion: (alias: string) => void;
  closeDrawer: () => void;
}>;

const JobDrawerVersionContent = (props: JobDrawerVersionContentProps) => {
  const {job, loading, error} = useJobDetails(props.jobId, props.jobVersion);

  if (error) {
    return <Alert severity="error">Error loading job: {error.message}</Alert>;
  }

  if (job == null || loading) {
    return <Loader active name="job-drawer-version-content-loader" />;
  }

  const versionDescription = getVersionDescription(
    job.versions,
    job.versionInfo.runTarget ?? ''
  );

  return (
    <>
      <S.LabelRow>
        <S.Label>Job version</S.Label>
      </S.LabelRow>
      <SelectJobVersion
        jobVersions={job.versions}
        selectedAlias={props.jobVersion}
        onSelectJobAlias={props.setJobVersion}
      />
      {versionDescription && (
        <>
          <S.LabelRow>
            <S.Label>Version description</S.Label>
          </S.LabelRow>
          <S.VersionDescription>
            <TruncatedMarkdown
              content={versionDescription}
              maxLength={280}
              tooltipPosition="left center"
            />
          </S.VersionDescription>
        </>
      )}
      {props.jobVersion != null && (
        <JobDrawerConfigureContent
          entityName={props.entityName}
          projectName={props.projectName}
          job={job}
          defaultRunToClone={props.defaultRunToClone}
          overrideHintText={props.overrideHintText}
          finalizeCTA={props.finalizeCTA}
          onFinalize={props.onFinalize}
          closeDrawer={props.closeDrawer}>
          {props.children}
        </JobDrawerConfigureContent>
      )}
    </>
  );
};

type JobDrawerConfigureContentProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  renderLaunchButton?: boolean;
  job: JobDetails;
  defaultRunToClone?: string;
  lockedRunToClone?: boolean;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  closeDrawer: () => void;
}>;

const JobDrawerConfigureContent = (props: JobDrawerConfigureContentProps) => {
  const jobIsPrivate: boolean =
    !props.job.projectAccess ||
    PRIVATE_ACCESS_TERMS.includes(props.job.projectAccess);
  const queryQueues = useViewerQueueOptions(jobIsPrivate, props.entityName);

  const queryColors = useProjectView(props.entityName, props.projectName);

  if (queryColors.error || queryQueues.error) {
    return <Alert severity="error">Error fetching job details</Alert>;
  }
  if (queryColors.loading || queryQueues.loading) {
    return <Loader active name="job-drawer-configure-content-loader" />;
  }
  if (props.job.versions.length === 0) {
    // At the moment we are disabling the launch button when this condition
    // occurs, so we don't expect to get here.
    return <Alert severity="info">This job has no versions to launch</Alert>;
  }

  return (
    <JobDrawerContentInner
      entityName={props.entityName}
      projectName={props.projectName}
      job={props.job}
      runToClone={props.defaultRunToClone}
      lockedRunToClone={props.lockedRunToClone}
      customRunColors={queryColors.customRunColors}
      queues={queryQueues.queues}
      finalizeCTA={props.finalizeCTA}
      overrideHintText={props.overrideHintText}
      onFinalize={props.onFinalize}
      onClose={props.closeDrawer}>
      {props.children}
    </JobDrawerContentInner>
  );
};

// A version of the same job drawer with the job and version locked.
export type JobDrawerContentLockedProps = React.PropsWithChildren<{
  closeDrawer: () => void;

  entityName: string;
  projectName: string;

  // required preselection of jobID and jobVersion in this LOCKED drawer
  jobId: string;
  jobVersion: string;

  defaultRunToClone?: string;

  // optional hint text underneath override bin
  overrideHintText?: string;

  finalizeCTA?: string; // Bottom right button text
  onFinalize?: (spec: LaunchSpec) => void; // Default: throws
}>;

export const JobDrawerContentLocked = ({
  entityName,
  projectName,
  jobId,
  jobVersion,
  defaultRunToClone,
  overrideHintText,
  finalizeCTA,
  closeDrawer,
  onFinalize,
  children,
}: JobDrawerContentLockedProps) => {
  const {job, loading, error} = useJobDetailsNoPoll(jobId, jobVersion);

  if (loading || !jobId) {
    return <Loader active name="job-drawer-content-locked-loader" />;
  }

  if (error) {
    return <Alert severity="error">Error loading job: {error.message}</Alert>;
  }

  if (job == null) {
    return <Alert severity="error">Error loading job</Alert>;
  }

  const jobOption = {
    value: job?.id,
    label: job?.name ?? '',
    entity: job.entityName,
    project: job.projectName,
  } as JobOption;

  return (
    <S.JobDrawer>
      <S.CenteredFlex>
        <S.Header>Launch "{jobOption?.label}"</S.Header>
        <S.CloseButton>
          <Button
            data-test="launch-drawer-close"
            onClick={() => {
              closeDrawer();
            }}
            icon="close"
            size="large"
            variant="ghost"
          />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.LabelRow>
        <S.Label>Job</S.Label>
      </S.LabelRow>
      <SelectJob
        entityName={job.entityName}
        projectName={job.projectName}
        selectedJobId={jobOption ?? null}
        onSelectJob={() => {}}
        disabled={true}
      />
      <JobDrawerVersionContentLocked
        job={job}
        jobAlias={jobVersion}
        defaultRunToClone={defaultRunToClone}
        entityName={job.entityName}
        projectName={job.projectName}
        overrideHintText={overrideHintText}
        finalizeCTA={finalizeCTA}
        closeDrawer={closeDrawer}
        onFinalize={onFinalize}>
        {children}
      </JobDrawerVersionContentLocked>
    </S.JobDrawer>
  );
};

type JobDrawerVersionContentLockedProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  job: JobDetails;
  jobAlias: string;
  defaultRunToClone?: string;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  closeDrawer: () => void;
}>;

const JobDrawerVersionContentLocked = (
  props: JobDrawerVersionContentLockedProps
) => {
  const versionDescription = getVersionDescription(
    props.job.versions,
    props.job.versionInfo.runTarget ?? ''
  );

  return (
    <>
      <S.LabelRow>
        <S.Label>Job version</S.Label>
      </S.LabelRow>
      <SelectJobVersion
        jobVersions={props.job.versions}
        selectedAlias={props.jobAlias}
        onSelectJobAlias={() => {}}
        disabled={true}
      />
      {versionDescription && (
        <>
          <S.LabelRow>
            <S.Label>Version description</S.Label>
          </S.LabelRow>
          <S.VersionDescription>
            <TruncatedMarkdown
              content={versionDescription}
              maxLength={280}
              tooltipPosition="left center"
            />
          </S.VersionDescription>
        </>
      )}
      <JobDrawerConfigureContent
        entityName={props.entityName}
        projectName={props.projectName}
        job={props.job}
        lockedRunToClone={true}
        defaultRunToClone={props.defaultRunToClone}
        overrideHintText={props.overrideHintText}
        finalizeCTA={props.finalizeCTA}
        onFinalize={props.onFinalize}
        closeDrawer={props.closeDrawer}>
        {props.children}
      </JobDrawerConfigureContent>
    </>
  );
};
